import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LANGS, setAppLanguage } from "../lib/i18n";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";

/**
 * LanguageSwitcher — globe-icon dropdown showing flag + native name.
 * On select:
 *   - immediately swaps i18next + persists to localStorage
 *   - if the user is logged in, fires PATCH /api/me/language to sync
 *     the preference across devices.
 *
 * Variant `compact` shrinks padding for the top-nav; default suits the
 * footer.
 */
export const LanguageSwitcher = ({ compact = false }) => {
  const { i18n, t } = useTranslation();
  const { token, refreshMe } = useAuth() || {};
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = SUPPORTED_LANGS.find((l) => l.code === i18n.language) || SUPPORTED_LANGS[0];

  const select = async (code) => {
    setAppLanguage(code);
    setOpen(false);
    if (token) {
      try {
        await axios.patch(`${API}/me/language`, { language: code });
        // Optimistic — refresh the user document so the new pref is in
        // context for any consumer that reads `user.language`.
        refreshMe && refreshMe();
      } catch {
        // Silent — local pref already applied.
      }
    }
  };

  return (
    <div ref={ref} className="relative inline-block" data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="language-switcher-trigger"
        title={t("language.label")}
        className={`inline-flex items-center gap-1.5 rounded-full border border-border bg-card hover:bg-muted text-foreground transition-colors ${
          compact ? "h-9 px-2.5 text-xs" : "h-10 px-3 text-sm"
        }`}
      >
        <Globe className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
        <span aria-hidden>{current.flag}</span>
        <span className="font-semibold uppercase tracking-wider">{current.code}</span>
      </button>

      {open && (
        <ul
          role="listbox"
          data-testid="language-switcher-menu"
          className="absolute right-0 mt-2 min-w-[180px] rounded-xl border border-border bg-popover text-popover-foreground shadow-lg overflow-hidden z-50"
        >
          {SUPPORTED_LANGS.map((l) => {
            const active = l.code === i18n.language;
            return (
              <li key={l.code} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => select(l.code)}
                  data-testid={`lang-option-${l.code}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors ${
                    active ? "font-semibold" : ""
                  }`}
                >
                  <span aria-hidden>{l.flag}</span>
                  <span className="flex-1 text-left">{t(l.labelKey)}</span>
                  {active && <Check className="w-3.5 h-3.5 text-primary" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
